/**
 * Runs the one persistence checkpoint a setup revision may trust: save the
 * profile, fetch it back by its authoritative key, and compare what storage
 * really holds against what was applied. A save reporting success proves
 * nothing on its own.
 *
 * The checkpoint knows nothing about DTOs, clients or React: callers supply
 * the save, the read-back and the observation as ports.
 */

import {
  expectProfileReadBack,
  profileReadBackMismatch,
  verifyProfileReadBack,
  type ProfileReadBackMismatchReason,
  type ProfileReadBackObservation,
} from '../../../shared/lib/setupRevision';

export interface ProfilePersistenceCheckpointPorts<TProfile> {
  /** The revision this checkpoint speaks for. */
  revisionId: number;
  /** The profile about to be saved, as the revision froze it. */
  intendedProfile: TProfile;
  /** Reads the connector setup a profile carries, in either direction. */
  observe: (profile: TProfile) => ProfileReadBackObservation;
  save: () => Promise<void>;
  /** `null` when no profile could be fetched for the authoritative key. */
  readBack: () => Promise<TProfile | null>;
  currentRevisionId: () => number;
  /** True once a newer revision has taken over from this one. */
  isSuperseded: () => boolean;
}

export type ProfilePersistenceCheckpoint<TProfile> =
  /** `setupFingerprint` is the setup this read-back confirmed, not a later re-read. */
  | { kind: 'verified'; profile: TProfile | null; setupFingerprint: string }
  | { kind: 'superseded' }
  | { kind: 'mismatch'; reason: ProfileReadBackMismatchReason; message: string };

const supersededCheckpoint = { kind: 'superseded' } as const;

/** A profile that could not be fetched holds no setup, rather than a different one. */
const unreadableProfile: ProfileReadBackObservation = { target: null, setupFingerprint: null };

/**
 * Whether the revision this checkpoint froze has been left behind - either
 * because the caller says so, or because a newer revision id is now current.
 * Checked after every await, so no branch can report against work it did not
 * perform.
 */
const hasMovedOn = <TProfile>(ports: ProfilePersistenceCheckpointPorts<TProfile>): boolean =>
  ports.isSuperseded() || ports.currentRevisionId() !== ports.revisionId;

export const runProfilePersistenceCheckpoint = async <TProfile>(
  ports: ProfilePersistenceCheckpointPorts<TProfile>
): Promise<ProfilePersistenceCheckpoint<TProfile>> => {
  const expectation = expectProfileReadBack(ports.observe(ports.intendedProfile), ports.revisionId);

  await ports.save();
  if (hasMovedOn(ports)) {
    return supersededCheckpoint;
  }

  const storedProfile = await ports.readBack();
  if (hasMovedOn(ports)) {
    return supersededCheckpoint;
  }

  if (!expectation) {
    // Nothing was applied that a read-back could confirm, so nothing is
    // confirmed: an unverifiable save must never pass as a verified one.
    return profileReadBackMismatch('setup-unverifiable');
  }

  const observed = storedProfile ? ports.observe(storedProfile) : unreadableProfile;
  const verdict = verifyProfileReadBack(expectation, observed, ports.currentRevisionId());
  if (verdict.kind === 'verified') {
    return {
      kind: 'verified',
      profile: storedProfile,
      setupFingerprint: expectation.setupFingerprint,
    };
  }
  if (verdict.kind === 'superseded') {
    return supersededCheckpoint;
  }

  return { kind: 'mismatch', reason: verdict.reason, message: verdict.message };
};
